import { isApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { HasuraGraphQLError } from '@/types/hasura';

const isHasuraGraphQLError = (err: GraphQLError): err is HasuraGraphQLError =>
  !!err.extensions && 'code' in err.extensions && 'path' in err.extensions;

const getGraphQLErrors = (err: unknown, tableName: string) => {
  const errors: Record<string, string> = {};

  if (!err) {
    console.warn('getGraphqlErrors: No error received');
    return errors;
  }

  if (!(err instanceof Error)) {
    console.warn('getGraphqlErrors: Error is not instance of Error');
    return errors;
  }

  if (!isApolloError(err)) {
    console.warn('getGraphqlErrors: Error is not an ApolloError');
    return errors;
  }

  err.graphQLErrors.forEach((error) => {
    // try parsing a message like
    // "Cannot query field \"foo\" on type \"User\"."
    const keys: string[] = [...error.message.matchAll(/"(.*?)"/g)].map((x) =>
      x[0].replaceAll('"', ''),
    );
    const cleanKeys = keys.map((key) =>
      key.replace(`${tableName}_`, '').replace('_key', ''),
    );
    if (cleanKeys.length > 0) {
      cleanKeys.forEach((key) => {
        if (isHasuraGraphQLError(error) && error.extensions.code) {
          errors[key] = `hasura_errors:${error.extensions.code}`;
        } else {
          errors[key] = `hasura_errors:UNKNOWN_ERROR`;
        }
      });
    }
  });
  return errors;
};

/**
 * Checks if the given error is a dataExceptionError
 * (custom error thrown by us from the db), and if it is
 * then returns the error key, otherwise returns null;
 *
 * @param err any Error object
 * @returns the error key which can be used as translation key
 */
export const getDataExceptionKey = (err: unknown) => {
  if (!(err instanceof Error)) {
    return null;
  }

  if (!isApolloError(err)) {
    return null;
  }

  const dataExceptionError = err.graphQLErrors.find(
    (e) => isHasuraGraphQLError(e) && e.extensions.code === 'data-exception',
  );

  if (!dataExceptionError) {
    return null;
  }

  return dataExceptionError.message.split('|')[0];
};

export default getGraphQLErrors;
